import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Login } from 'client-core';

export type MovedLogin = {
  login: Login;
  oldPath: string;
  operation:
    | {
        kind: 'move';
        newVaultLabel: string;
        newPath?: string;
        newTag?: string;
      }
    | {
        kind: 'delete';
      }
    | {
        kind: 'transfer';
        recipientEmail: string;
      };
};

export interface MovedLoginContextValue {
  movedLogin: MovedLogin | undefined;
  markActiveLoginMoved: (newMovedLogin: Omit<MovedLogin, 'oldPath'>) => void;
  updateMovedLoginNewPath: (newPath: string) => void;
}

export const MovedLoginContext = createContext<MovedLoginContextValue>({
  movedLogin: undefined,
  markActiveLoginMoved: () => {},
  updateMovedLoginNewPath: () => {},
});

export function MovedLoginContextProvider({
  children,
}: React.PropsWithChildren<{ override?: Partial<MovedLoginContextValue> }>) {
  const [movedLogin, setMovedLogin] = useState<MovedLogin>();

  const location = useLocation();
  useEffect(() => {
    if (movedLogin && location.pathname !== movedLogin.oldPath) {
      setMovedLogin(undefined);
    }
  }, [location.pathname, movedLogin]);

  const markActiveLoginMoved = useCallback(
    (newMovedLogin: Omit<MovedLogin, 'oldPath'>) => {
      setMovedLogin({ oldPath: location.pathname, ...newMovedLogin });
    },
    [location.pathname],
  );

  const updateNewPath = useCallback((newPath: string) => {
    setMovedLogin((ml) =>
      ml && ml.operation.kind === 'move' ? { ...ml, operation: { ...ml.operation, newPath } } : ml,
    );
  }, []);

  return (
    <MovedLoginContext.Provider
      value={{ movedLogin, markActiveLoginMoved, updateMovedLoginNewPath: updateNewPath }}
    >
      {children}
    </MovedLoginContext.Provider>
  );
}

export function useMovedLoginContext() {
  return useContext(MovedLoginContext);
}
